import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClockCounterClockwise, FileText, Folder, GitBranch } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActivityLogs = ({ user }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchActivities();
  }, [filter]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      let url = `${API}/activity?limit=100`;
      if (filter !== 'all') {
        url += `&resource_type=${filter}`;
      }
      const response = await axios.get(url);
      setActivities(response.data);
    } catch (error) {
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-'; return new Date(String(dateString).endsWith('Z') || String(dateString).includes('+') ? dateString : dateString + 'Z').toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (resourceType) => {
    const iconClass = "w-9 h-9 rounded-lg flex items-center justify-center";
    switch (resourceType) {
      case 'document':
        return (
          <div className={`${iconClass} bg-blue-50`}>
            <FileText size={18} weight="regular" className="text-blue-600" />
          </div>
        );
      case 'folder':
        return (
          <div className={`${iconClass} bg-amber-50`}>
            <Folder size={18} weight="regular" className="text-amber-600" />
          </div>
        );
      case 'workflow':
        return (
          <div className={`${iconClass} bg-purple-50`}>
            <GitBranch size={18} weight="regular" className="text-purple-600" />
          </div>
        );
      default:
        return (
          <div className={`${iconClass} bg-gray-100`}>
            <ClockCounterClockwise size={18} weight="regular" className="text-gray-600" />
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="activity-logs-page">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 mb-1">
          Activity
        </h1>
        <p className="text-gray-500">Track all system activities and changes</p>
      </div>
      
      {/* Filter Buttons */}
      <div className="mb-6 flex gap-2">
        {['all', 'document', 'folder'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-sm rounded-xl transition-all ${
              filter === f 
                ? 'bg-blue-900 text-white' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1) + 's'}
          </button>
        ))}
      </div>
      
      {activities.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-apple-sm">
          <ClockCounterClockwise size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No activity logs found</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-apple-sm overflow-hidden divide-y divide-gray-50">
          {activities.map((activity) => (
            <div key={activity.id} className="px-5 py-4 hover:bg-gray-50 transition-colors" data-testid={`activity-${activity.id}`}>
              <div className="flex items-start gap-4">
                {getActionIcon(activity.resource_type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-gray-900">{activity.user_name}</span>
                    {' '}{activity.action}{' '}
                    <span className="font-medium text-gray-900">{activity.resource_name}</span>
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-400">{formatDate(activity.timestamp)}</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-md capitalize">{activity.resource_type}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityLogs;
